import React, { useEffect, useState, useCallback } from "react";
import { X, Star, Crown, Gem } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const CustomerDashboard = () => {
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  // Retrieve Profile Data
  const customerName = localStorage.getItem("customerName");
  const customerEmail = localStorage.getItem("customerEmail");
  const customerPhone = localStorage.getItem("customerPhone");

  /* ================= LOGOUT ================= */
  const logout = useCallback(() => {
    localStorage.removeItem("customerToken");
    localStorage.removeItem("customerName");
    localStorage.removeItem("customerEmail");
    localStorage.removeItem("customerPhone");
    navigate("/customer-login");
  }, [navigate]);

  /* ================= FETCH ENROLLMENTS ================= */
  const fetchEnrollments = useCallback(async () => {
    const token = localStorage.getItem("customerToken");

    if (!token) {
      navigate("/customer-login");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
      const res = await fetch(
        `${API_BASE}/api/customer/enrollments`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      /* === Token expired or unauthorized === */
      if (res.status === 401 || res.status === 403) {
        logout();
        return;
      }

      const data = await res.json();

      if (res.ok) {
        setEnrollments(data.enrollments || []);
        setCurrentPage(1); // Reset to first page on new data fetch
      } else {
        setError(data.message || "Failed to load enrollments");
      }
    } catch (err) {
      console.error("Enrollment fetch error:", err);
      setError("Server not responding. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [navigate, logout]);

  /* ================= USE EFFECT ================= */
  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  // Computed Pagination
  const totalPages = Math.ceil(enrollments.length / itemsPerPage);
  const displayedEnrollments = enrollments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  /* ================= HELPERS ================= */
  const isSessionExpired = (dateString) => {
    if (!dateString) return false;
    const sessionDate = new Date(dateString);
    const today = new Date();
    // Compare dates only (ignore time)
    const sDate = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
    const tDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return sDate < tDate;
  };

  /* ================= HELPERS END ================= */

  const getCourseDesign = (courseName) => {
    const name = courseName?.toLowerCase() || "";

    // --- DIAMOND TIER ---
    if (name.includes("legacy")) {
      return {
        tier: "DIAMOND",
        label: "VIP Transformation Programme",
        icon: Gem,
        badgeColor: "bg-blue-50 text-blue-800 border-blue-100",
        cardBorder: "border-yellow-200 hover:border-yellow-400",
        cardBg: "bg-gradient-to-br from-yellow-50/50 to-white"
      };
    }

    // --- GOLD TIER ---
    if (name.includes("advance leadership")) {
      return {
        tier: "GOLD",
        label: "Leadership Transformation",
        icon: Crown,
        badgeColor: "bg-yellow-100 text-yellow-800 border-yellow-200",
        cardBorder: "border-purple-100 hover:border-purple-300",
        cardBg: "bg-purple-50/30"
      };
    }
    if (name.includes("elite")) {
      return {
        tier: "GOLD",
        label: "Advance Programme",
        icon: Crown,
        badgeColor: "bg-yellow-100 text-yellow-800 border-yellow-200",
        cardBorder: "border-purple-100 hover:border-purple-300",
        cardBg: "bg-purple-50/30"
      };
    }

    // --- SILVER TIER ---
    if (name.includes("leadership growth")) {
      return {
        tier: "SILVER",
        label: "High-Performance Leadership",
        icon: Star,
        badgeColor: "bg-gray-100 text-gray-600 border-gray-200",
        cardBorder: "border-gray-100 hover:border-purple-200",
        cardBg: "bg-white"
      };
    }
    if (name.includes("special growth")) {
      return {
        tier: "SILVER",
        label: "Standard Programme",
        icon: Star,
        badgeColor: "bg-gray-100 text-gray-600 border-gray-200",
        cardBorder: "border-gray-100 hover:border-purple-200",
        cardBg: "bg-white"
      };
    }

    // --- STANDARD ---
    return {
      tier: "STANDARD",
      label: "Active Course",
      icon: null,
      badgeColor: "bg-gray-50 text-gray-500",
      cardBorder: "border-gray-100",
      cardBg: "bg-white"
    };
  };

  /* ================= UI ================= */
  return (
    <div className="flex flex-col min-h-screen font-sans bg-gray-50">
      <Navbar hideLogin={true} />

      <main className="flex-grow p-6 lg:p-12">
        <div className="max-w-6xl mx-auto">
          {/* HEADER */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                My Enrolled Courses
              </h2>
              <p className="text-gray-500 text-sm mt-1">Manage and view your active learning paths.</p>
            </div>

            <div className="flex items-center gap-6">
              {/* User Profile Display */}
              {customerName && (
                <div className="flex flex-col items-center md:items-end text-center md:text-right border-r-0 md:border-r border-gray-200 pr-0 md:pr-6 mr-0 md:mr-2">
                  <p className="text-sm font-bold text-gray-900 leading-none mb-1">{customerName}</p>
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 font-medium">{customerEmail}</span>
                    {customerPhone && (
                      <span className="text-[10px] text-gray-400 font-mono tracking-wider mt-0.5">
                        {customerPhone}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={logout}
                className="bg-gray-900 text-white px-6 py-2.5 rounded-xl font-bold uppercase tracking-wider text-xs hover:bg-black transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center gap-2"
              >
                <span>Logout</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" x2="9" y1="12" y2="12" /></svg>
              </button>
            </div>
          </div>

          {/* LOADING */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-exiser-primary"></div>
            </div>
          )}

          {/* ERROR */}
          {!loading && !error && enrollments.length === 0 && (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
              <p className="text-gray-400 text-lg mb-4">You haven't enrolled in any courses yet.</p>
              <button onClick={() => navigate('/categories')} className="text-exiser-primary font-bold hover:underline">
                Browse Courses
              </button>
            </div>
          )}

          {/* ENROLLMENT LIST */}
          {!loading && !error && enrollments.length > 0 && (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {displayedEnrollments.map((course, index) => {
                  const design = getCourseDesign(course.courseName);
                  const TierIcon = design.icon;

                  return (
                    <div
                      key={index}
                      className={`${design.cardBg} border ${design.cardBorder} rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all group duration-300 flex flex-col h-full relative overflow-hidden`}
                    >
                      {/* Decorative Background for Diamond */}
                      {design.tier === 'DIAMOND' && (
                        <div className="absolute top-0 right-0 w-20 h-20 bg-yellow-100 rounded-full blur-2xl -mr-10 -mt-10 opacity-50 pointer-events-none"></div>
                      )}

                      <div className="flex justify-between items-start mb-4 relative z-10">
                        <div className="flex flex-col items-start gap-1">
                          {design.tier !== 'STANDARD' && (
                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] border font-bold uppercase tracking-wider ${design.badgeColor}`}>
                              {TierIcon && <TierIcon className="w-2.5 h-2.5 fill-current" />} {design.tier}
                            </span>
                          )}
                          <p className={`text-[10px] uppercase tracking-wider font-bold ${design.tier === 'DIAMOND' ? 'text-yellow-700' : 'text-gray-400'}`}>
                            {design.label}
                          </p>
                        </div>

                        {course.amount === 0 ? (
                          <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded-md">FREE</span>
                        ) : (
                          <span className="text-xs font-bold text-gray-900">₹{course.amount}</span>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight group-hover:text-exiser-primary transition-colors relative z-10">
                        {course.courseName === 'Identity Transformation Lab' ? 'Growth Mastery' :
                          course.courseName === 'Daily Scientific Visualization' || course.courseName === 'Daily Scientific Visualization (Monthly)' ? 'Creative Manifestation Practice' :
                            course.courseName}
                      </h3>

                      <div className="space-y-3 mb-6 flex-grow relative z-10">
                        {/* Date */}
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <div className="w-8 h-8 rounded-full bg-white/50 border border-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Date</p>
                            <p className="font-semibold">{course.date ? new Date(course.date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : "Date not scheduled"}</p>
                          </div>
                        </div>

                        {/* Time */}
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <div className="w-8 h-8 rounded-full bg-white/50 border border-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                          </div>
                          <div>
                            <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Time</p>
                            <p className="font-semibold">{course.time || "Time TBD"}</p>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-gray-100/50 flex items-center justify-between mt-auto relative z-10">
                        <button
                          className="text-xs font-bold text-gray-500 hover:text-gray-900 transition-colors"
                          onClick={() => setSelectedEnrollment(course)}
                        >
                          View Details
                        </button>

                        <div className="flex items-center gap-2">
                          {isSessionExpired(course.date) ? (
                            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
                              Session Ended
                            </span>
                          ) : course.video_link ? (
                            <a
                              href={course.video_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-exiser-primary text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-purple-700 transition-all shadow-md shadow-purple-900/10 flex items-center gap-2"
                              title="Join Meeting"
                            >
                              <span>Join Session</span>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15.6 11.6L22 7v10l-6.4-4.5v-1z" /><rect x="2" y="5" width="14" height="14" rx="2" ry="2" /></svg>
                            </a>
                          ) : (
                            <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-3 py-1.5 rounded-lg">
                              Link Pending
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION CONTROLS */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Previous
                  </button>
                  <span className="text-sm font-medium text-gray-600 bg-white px-3 py-1 rounded-lg border border-gray-100">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />

      {/* ================= DETAILS MODAL ================= */}
      {selectedEnrollment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative animate-scaleIn">
            <button
              onClick={() => setSelectedEnrollment(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors bg-gray-100 p-1 rounded-full"
            >
              <X size={20} />
            </button>

            <div className="mb-6 border-b border-gray-100 pb-4">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${selectedEnrollment.status === 'CONFIRMED' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-exiser-primary'
                }`}>
                {selectedEnrollment.status || "Active"}
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mt-2">
                {selectedEnrollment.courseName === 'Identity Transformation Lab' ? 'Growth Mastery' :
                  selectedEnrollment.courseName === 'Daily Scientific Visualization' || selectedEnrollment.courseName === 'Daily Scientific Visualization (Monthly)' ? 'Creative Manifestation Practice' :
                    selectedEnrollment.courseName}
              </h3>
              <p className="text-xs text-gray-500 mt-1 uppercase tracking-wider">Booking Ref: {selectedEnrollment.booking_code || selectedEnrollment.id || "N/A"}</p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-xl flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Date</span>
                  <span className="text-gray-900 font-semibold">
                    {selectedEnrollment.date ? new Date(selectedEnrollment.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : "Date not scheduled"}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Time</span>
                  <span className="text-gray-900 font-semibold">{selectedEnrollment.time || "Time TBD"}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 border border-gray-100 rounded-xl">
                  <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Fee Paid</span>
                  <span className="text-gray-900 font-bold">{selectedEnrollment.amount === 0 ? "FREE" : `₹${selectedEnrollment.amount}`}</span>
                </div>
                <div className="p-3 border border-gray-100 rounded-xl">
                  <span className="text-xs text-gray-400 uppercase font-bold block mb-1">Booked On</span>
                  <span className="text-gray-900 font-medium text-sm">
                    {selectedEnrollment.created_at ? new Date(selectedEnrollment.created_at).toLocaleDateString() : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setSelectedEnrollment(null)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              {/* Modal Action in Footer */}
              {isSessionExpired(selectedEnrollment.date) ? (
                <button disabled className="flex-1 py-3 bg-gray-100 text-gray-400 font-bold rounded-xl cursor-not-allowed border border-gray-200">
                  Session Ended
                </button>
              ) : selectedEnrollment.video_link ? (
                <a
                  href={selectedEnrollment.video_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-3 bg-exiser-primary text-white font-bold rounded-xl hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                >
                  Join Session
                </a>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;